// ViewCube — Fusion 360-style куб ориентации в правом-верхнем углу.
//
// Отдельный мини-Canvas (160×160) с собственной камерой и сценой.
// Камера ViewCube СИНХРОНИЗИРУЕТСЯ с основной камерой через rAF и
// window.__kubikonCadCameraMatrix() — на каждом кадре куб смотрит в ту же
// сторону что и юзер.
//
// Клик по грани → window.__kubikonCadCamera.setView(...) меняет основную камеру.
// Стрелки 90° / «домик» — отдельные DOM-кнопки (ViewCubeOverlay), они тоже
// дёргают bridge.
//
// Внутри Canvas: <ViewCubeMesh/> — кубик 1×1×1 с 6 текстурами-подписями
// (Canvas2D rendered → CanvasTexture).

import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

import cl from './ViewCube.module.css';


// === Текстура грани куба (нормальная или hover-вариант) ===
// Большой canvas (512×512) + крупный жирный шрифт + сильная anisotropy →
// надписи читаются на маленьком кубике 110×110 даже под углом.
function makeFaceTexture(label, hover = false) {
    const SIZE = 512;
    const c = document.createElement('canvas');
    c.width = c.height = SIZE;
    const ctx = c.getContext('2d');
    // Чистая заливка (без градиента — повышает контраст текста)
    ctx.fillStyle = hover ? '#3a78ff' : '#ffffff';
    ctx.fillRect(0, 0, SIZE, SIZE);
    // Рамка — тонкая, тёмная
    ctx.strokeStyle = hover ? '#1a4fcc' : '#5a7090';
    ctx.lineWidth = 8;
    ctx.strokeRect(4, 4, SIZE - 8, SIZE - 8);
    // Подпись — крупный жирный шрифт, авто-уменьшение если не вписывается
    ctx.fillStyle = hover ? '#ffffff' : '#0d1828';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let fontSize = 96;
    const maxWidth = SIZE - 80; // отступ 40px с каждой стороны
    do {
        ctx.font = `800 ${fontSize}px "Inter", "Segoe UI", system-ui, sans-serif`;
        if (ctx.measureText(label).width <= maxWidth) break;
        fontSize -= 4;
    } while (fontSize > 40);
    ctx.fillText(label, SIZE / 2, SIZE / 2);

    const tex = new THREE.CanvasTexture(c);
    tex.anisotropy = 16;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    return tex;
}


// Порядок материалов в BoxGeometry: +X, -X, +Y, -Y, +Z, -Z
// Соответствие наших осей видам (камера по умолчанию смотрит из +Y или ISO,
// up = +Z):
//   +X = СПРАВА    -X = СЛЕВА
//   +Y = СЗАДИ     -Y = СПЕРЕДИ
//   +Z = СВЕРХУ    -Z = СНИЗУ
const FACE_LABELS = ['СПРАВА', 'СЛЕВА', 'СЗАДИ', 'СПЕРЕДИ', 'СВЕРХУ', 'СНИЗУ'];
const FACE_VIEWS  = ['right', 'left', 'back', 'front', 'top', 'bottom'];


// === Mesh ViewCube'а ===
// Куб 1×1×1 с поворотом, который КАЖДЫЙ кадр копируется из основной камеры.
const ViewCubeMesh = () => {
    const meshRef = useRef(null);
    const [hovered, setHovered] = useState(-1);

    // Текстуры (нормальные и hover) — создаются один раз
    const { normalTextures, hoverTextures } = useMemo(() => ({
        normalTextures: FACE_LABELS.map((l) => makeFaceTexture(l, false)),
        hoverTextures:  FACE_LABELS.map((l) => makeFaceTexture(l, true)),
    }), []);

    // Cleanup на unmount
    useEffect(() => () => {
        normalTextures.forEach((t) => t.dispose());
        hoverTextures.forEach((t) => t.dispose());
    }, [normalTextures, hoverTextures]);

    // Синхронизация поворота куба с основной камерой.
    //
    // Идея:
    //   ViewCubeMesh должен показывать ту же ориентацию что и сцена.
    //   Основная камера имеет rotation M (3x3) — она преобразует МИР→ВИД.
    //   Мини-камера ViewCube'а зафиксирована в (0,0,3.2) и смотрит в (0,0,0)
    //   с up=(0,1,0) — т.е. её rotation = identity.
    //
    //   Чтобы кубик показывал ту же сторону что и большая сцена, его
    //   ЛОКАЛЬНАЯ rotation = M (а НЕ inv(M)). Потому что:
    //     vec_view_main = inv(M_main) · vec_world
    //     vec_view_mini = inv(M_mini) · M_cube · vec_local
    //   Чтобы они совпали (vec_view_main == vec_view_mini), при vec_local = vec_world:
    //     inv(M_main) = inv(M_mini) · M_cube
    //     M_cube = M_mini · inv(M_main)
    //   M_mini = identity → M_cube = inv(M_main).
    //
    // Берём camera worldMatrix → извлекаем rotation → инвертируем.
    useFrame(() => {
        if (!meshRef.current) return;
        const getMatrix = window.__kubikonCadCameraMatrix;
        if (!getMatrix) return;
        const e = getMatrix();
        if (!e || e.length < 16) return;
        // Копируем 16 чисел из массива в Matrix4
        const m = new THREE.Matrix4().fromArray(e);
        // Извлекаем чистую rotation (убрав translate/scale)
        const rot = new THREE.Matrix4().extractRotation(m);
        const q = new THREE.Quaternion().setFromRotationMatrix(rot).invert();
        meshRef.current.quaternion.copy(q);
    });

    const onClick = (e) => {
        e.stopPropagation();
        const idx = e.face?.materialIndex ?? -1;
        const view = FACE_VIEWS[idx];
        if (!view) return;
        const api = window.__kubikonCadCamera;
        if (api?.setView) api.setView(view);
    };

    const onMove = (e) => {
        const idx = e.face?.materialIndex ?? -1;
        if (idx !== hovered) setHovered(idx);
    };

    const onOut = () => setHovered(-1);

    return (
        <group ref={meshRef}>
            <mesh
                onClick={onClick}
                onPointerMove={onMove}
                onPointerOut={onOut}
            >
                <boxGeometry args={[1, 1, 1]}/>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                    <meshBasicMaterial
                        key={i}
                        attach={`material-${i}`}
                        map={hovered === i ? hoverTextures[i] : normalTextures[i]}
                    />
                ))}
            </mesh>
            {/* Тонкая тёмная окантовка рёбер — крутится вместе с кубом */}
            <lineSegments>
                <edgesGeometry args={[new THREE.BoxGeometry(1.001, 1.001, 1.001)]}/>
                <lineBasicMaterial color="#1a2540" linewidth={1}/>
            </lineSegments>
        </group>
    );
};


// === Камера мини-Canvas: фиксированная позиция ===
// Просто стоит сверху-сбоку и смотрит в (0,0,0). Поворот куба делает sync.
const MiniCameraSetup = () => {
    const { camera } = useThree();
    useEffect(() => {
        camera.position.set(0, 0, 3.2);
        camera.lookAt(0, 0, 0);
        camera.up.set(0, 1, 0);
        camera.updateProjectionMatrix();
    }, [camera]);
    return null;
};


/** Главный экспорт — DOM-обёртка с мини-Canvas + кнопки управления.
 *  Лежит в правом-верхнем углу viewport'а. */
const ViewCubeOverlay = () => {
    const callApi = (method, ...args) => {
        const api = window.__kubikonCadCamera;
        if (!api || typeof api[method] !== 'function') return;
        try { api[method](...args); } catch (_) {}
    };

    const rotate = (dir) => () => callApi('rotateView', dir);
    const home = () => callApi('fit');

    return (
        <div className={cl.wrap}>
            {/* Мини-Canvas с кубом.
                frameloop=always — иначе sync-каждый-кадр не работает.
                gl.alpha=true + onCreated setClearAlpha(0) — прозрачный фон. */}
            <div className={cl.cubeContainer}>
                <Canvas
                    camera={{ position: [0, 0, 3.2], fov: 35 }}
                    gl={{ antialias: true, alpha: true, premultipliedAlpha: false }}
                    dpr={[1, 2]}
                    frameloop="always"
                    onCreated={({ gl }) => {
                        gl.setClearColor(0x000000, 0);
                    }}
                >
                    <MiniCameraSetup/>
                    <ambientLight intensity={1}/>
                    <ViewCubeMesh/>
                </Canvas>
            </div>

            {/* Стрелки 90° */}
            <button className={`${cl.arrow} ${cl.arrowTop}`}    onClick={rotate('up')}    title="Повернуть вверх">▲</button>
            <button className={`${cl.arrow} ${cl.arrowBottom}`} onClick={rotate('down')}  title="Повернуть вниз">▼</button>
            <button className={`${cl.arrow} ${cl.arrowLeft}`}   onClick={rotate('ccw')}   title="Повернуть влево">◀</button>
            <button className={`${cl.arrow} ${cl.arrowRight}`}  onClick={rotate('cw')}    title="Повернуть вправо">▶</button>

            {/* Кнопка домик */}
            <button className={cl.homeBtn} onClick={home} title="Исходный вид">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M2 8 L8 3 L14 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M4 7.5 L4 13 L12 13 L12 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
        </div>
    );
};

export { ViewCubeOverlay };
export default ViewCubeOverlay;
